import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hook';
import { BannerHelper } from '@/components/common/BannerToast';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { selectCurrentDataState } from '../../create.slice';

function AdditionalInfo() {
  const { t } = useTranslation();
  const currentData = useAppSelector(selectCurrentDataState);
  const { setValue, formState, control, register } = useFormContext();

  const labelMapper: Record<string, string> = {
    company_name: t('Wizard/validationLabels/key8'),
    agent_name: t(currentData.bot_code == BotTypeEnum.HUMAN_CLONE ? 'Wizard/validationLabels/key14' : 'Wizard/validationLabels/key9'),
    domain: t('Wizard/validationLabels/key10'),
    personality: t('Wizard/validationLabels/key11'),
    legal_sector: t('Wizard/validationLabels/key12'),
    business_sector: t('Wizard/validationLabels/key13'),
    disclaimer: t('Wizard/validationLabels/key31'),
    products_services: t('Wizard/validationLabels/key32'),
    brand: t('Wizard/validationLabels/key33'),
    target_audience: t('Wizard/validationLabels/key34'),
    subject_expertise: t('Wizard/validationLabels/key35'),
    education_level: t('Wizard/validationLabels/key36'),
    tech_specialization: t('Wizard/validationLabels/key37'),
    specialization: t('Wizard/validationLabels/key38'),
    industry_focus: t('Wizard/validationLabels/key39'),
    market_area: t('Wizard/validationLabels/key40'),
    establishment_name: t('Wizard/validationLabels/key41'),
    cuisine_type: t('Wizard/validationLabels/key42'),
    event_specialization: t('Wizard/validationLabels/key43'),
    agency_name: t('agency_name'),
  };

  const capitalizeAndRemoveUnderscore = (str: string) => {
    try {
      return str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    } catch (error) {
      return str;
    }
  };

  const getErrorMessage = (errors: any, name: string) => {
    if (errors.instructions?.[name]?.type == 'max') {
      return t('Wizard/validationLabels/key16');
    }

    if (currentData.bot_code == BotTypeEnum.HUMAN_CLONE && name === 'agent_name') {
      return t('Wizard/validationLabels/key15', {
        variable: t('Wizard/validationLabels/key14'),
      });
    }

    return t('Wizard/validationLabels/key15', {
      variable: labelMapper[name],
    });
  };

  useEffect(() => {
    if (currentData) {
      setValue('assistant', currentData?.assistant);
      const instructions = currentData?.instructions;
      if (instructions) {
        Object.entries(instructions).forEach(([key, value]) => {
          setValue('instructions', {
            ...(currentData.instructions || {}),
            [key]: value || '',
          });
        });
      }
    }
  }, [currentData, setValue]);

  return (
    <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-inline-flex md:tw-w-2/3 tw-w-full">
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {t('Wizard/AiAdditionalInfo/key1')}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('Wizard/AiAdditionalInfo/key2')}
            {t('Wizard/AiAdditionalInfo/key3')}
          </div>
        </div>
      </div>

      <Form.Group className="tw-mb-3">
        <Form.Label>{t('Wizard/AiAdditionalInfo/key5')}</Form.Label>
        <Form.Control
          as={'textarea'}
          maxLength={500}
          placeholder={t('Wizard/AiAdditionalInfo/key6').toString()}
          defaultValue={currentData?.welcoming ?? ''}
          {...register('welcoming')}
        />
      </Form.Group>

      <Form.Group className="tw-mb-3">
        <Form.Label>{t('Wizard/AiAdditionalInfo/key4')}</Form.Label>
        <div className="tw-mb-4">
          <BannerHelper message={t('HelpBanner/AdditionalInfo/hint')} />
        </div>
        <Form.Control
          placeholder={t('KnowledgeBasesCreateAssistantBot/ph-01')}
          type="text"
          as={'textarea'}
          maxLength={10000}
          rows={12}
          defaultValue={''}
          {...register('additional_instructions')}
        />
      </Form.Group>

      {currentData?.required_instructions?.map(ri => (
        <Form.Group key={ri.name} className="tw-mb-3">
          <Form.Label>{capitalizeAndRemoveUnderscore(labelMapper[ri.name])} *</Form.Label>

          <Controller
            name={`instructions.${ri.name}`}
            control={control}
            render={({ field }) => (
              <Form.Control
                {...field}
                as={ri.name === 'personality' ? 'textarea' : undefined}
                rows={ri.name === 'personality' ? 5 : undefined}
                type="text"
                defaultValue={''}
                placeholder={ri.description}
                isInvalid={!!(formState.errors.instructions as any)?.[ri.name]}
              />
            )}
          />

          <Form.Control.Feedback type="invalid">{getErrorMessage(formState.errors, ri.name)}</Form.Control.Feedback>
        </Form.Group>
      ))}
    </div>
  );
}

export default WrappedErrorBoundary(AdditionalInfo);
